import DeleteIcon from '@mui/icons-material/Delete'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SettingsIcon from '@mui/icons-material/Settings'
import GroupIcon from '@mui/icons-material/Group'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import type { ChangeEvent } from 'react'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FooterDCP } from '../components/FooterDCP'
import { ModalConfirmDialogDCP } from '../components/ModalConfirmDialogDCP'
import { loadStatus } from '../constants/loadStatus'
import { errorMsg } from '../constants/messages'
import { useAuth } from '../hooks/useAuth'
import { useC213DeviceGet } from '../hooks/useC213DeviceGet'
import { useC214DeviceMemoUpdate } from '../hooks/useC214DeviceMemoUpdate'
import { useC215DeviceDelete } from '../hooks/useC215DeviceDelete'
import { useC218DeviceBulkImport } from '../hooks/useC218DeviceBulkImport'
import { useC109MyInfoGet } from '../hooks/useC109MyInfoGet'
import { useC902SignedUrlGet } from '../hooks/useC902SignedUrlGet'
import { useCloudStorage } from '../hooks/useCloudStorage'
import { GroupAssignmentDialog } from '../components/GroupAssignmentDialog'
import { OperationPermissionDialog } from '../components/OperationPermissionDialog'
import {
  checkboxPrimarySx,
  deviceListActionButtonSx,
  successSnackbarAlertSx,
  dialogCancelButtonSx,
  dialogSaveButtonSx,
  dialogActionsSx,
} from '../constants/uiSx'
import { validateImportFileSize } from '../util/FileSize'
import { LanguageContext } from '../util/LanguageContext'

const zipPattern = /.(zip)$/i

type TenantDevice = {
  device_id: string
  cotopat_id?: string
  device_type?: string
  memo?: string
  group_ids?: string[]
  start_date?: number
  end_date?: number
  license_type?: string
  is_license_active?: boolean
  rhq?: string
  sales_company?: string
}

type TenantDeviceListResponse = {
  tenant?: string
  devices?: TenantDevice[]
}

export const DeviceList = () => {
  const auth = useAuth()
  const navigate = useNavigate()
  const translation = useContext(LanguageContext)

  const c213 = useC213DeviceGet()
  const c214 = useC214DeviceMemoUpdate()
  const c215 = useC215DeviceDelete()
  const c218 = useC218DeviceBulkImport()
  const c109 = useC109MyInfoGet()
  const c902 = useC902SignedUrlGet()
  const cs = useCloudStorage()

  const [devices, setDevices] = useState<TenantDevice[]>([])
  const [myRole, setMyRole] = useState<number | null>(null)
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([])
  const [memoMap, setMemoMap] = useState<Record<string, string>>({})
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [groupDialogOpen, setGroupDialogOpen] = useState(false)
  const [operationDialogOpen, setOperationDialogOpen] = useState(false)
  const [groupSaveNoticeOpen, setGroupSaveNoticeOpen] = useState(false)
  const [operationSaveNoticeOpen, setOperationSaveNoticeOpen] = useState(false)
  const [deleteSuccessNoticeOpen, setDeleteSuccessNoticeOpen] = useState(false)
  const [importSuccessNoticeOpen, setImportSuccessNoticeOpen] = useState(false)
  const [openErrorDialog, setOpenErrorDialog] = useState(false)
  const [errorDialogText, setErrorDialogText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const loading =
    c213.loading === loadStatus.LOADING ||
    c214.loading === loadStatus.LOADING ||
    c215.loading === loadStatus.LOADING ||
    c218.loading === loadStatus.LOADING ||
    c109.loading === loadStatus.LOADING ||
    c902.loading === loadStatus.LOADING ||
    cs.loading === loadStatus.LOADING

  const canManageDevices = myRole === 1

  useEffect(() => {
    auth.setIsDisable(loading)
  }, [auth, loading])

  const refreshDevices = async () => {
    const myInfoResult = await c109.execMyInfoGet(auth.tenant)
    if (!myInfoResult.success) {
      setErrorDialogText(myInfoResult.message ?? errorMsg.SYSTEM_ERROR)
      setOpenErrorDialog(true)
      return
    }

    const role = myInfoResult.info?.role
    if (role !== 1 && role !== 2) {
      setErrorDialogText(errorMsg.SYSTEM_ERROR)
      setOpenErrorDialog(true)
      return
    }
    setMyRole(role)

    const result = await c213.execDeviceGet(auth.tenant)
    if (!result.success) {
      setErrorDialogText(result.message ?? errorMsg.SYSTEM_ERROR)
      setOpenErrorDialog(true)
      return
    }

    const responseData = result.info as TenantDevice[] | TenantDeviceListResponse | undefined
    const rawDevices = Array.isArray(responseData) ? responseData : (responseData?.devices ?? [])
    const resultDevices: TenantDevice[] = rawDevices
      .filter((d): d is TenantDevice => !!d.device_id)
      .map(d => ({ ...d, device_id: d.device_id }))

    setDevices(resultDevices)
    setMemoMap(
      resultDevices.reduce<Record<string, string>>((acc, device) => {
        acc[device.device_id] = device.memo ?? ''
        return acc
      }, {}),
    )
    setSelectedDeviceIds(prev =>
      prev.filter(id => resultDevices.some(device => device.device_id === id)),
    )
  }

  useEffect(() => {
    refreshDevices()
  }, [auth.tenant])

  useEffect(() => {
    if (!canManageDevices) {
      setSelectedDeviceIds([])
    }
  }, [canManageDevices])

  const formatYmdByLocale = (
    unixSeconds: number | null | undefined,
    locale: string,
    fallback: string
  ) => {
    if (unixSeconds == null) return fallback
    const d = new Date(unixSeconds * 1000) // 秒 → ms
    // Invalid Date 対策
    if (Number.isNaN(d.getTime())) return fallback

    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d)
  }

  const parsedRows = useMemo(() => {
    const userLanguage = navigator.language || navigator.languages?.[0] || 'en-US'

    return devices.map(device => {
      const normalizedDeviceType = (device.device_type ?? '').toLowerCase()
      const deviceType =
        normalizedDeviceType === 'screen'
          ? 'Screen'
          : normalizedDeviceType === 'mobile'
            ? 'Mobile'
            : (device.device_type ?? '')

      const isLicenseActive = device.is_license_active !== false
      const unknown = translation['label_contract_period_unknown']

      const startDate = formatYmdByLocale(device.start_date, userLanguage, unknown)
      const endDate = formatYmdByLocale(device.end_date, userLanguage, unknown)

      let contractPeriod = unknown
      if (device.license_type === 'subscription') {
        if (endDate === unknown) {
          contractPeriod = translation['label_contract_period_subscription']
        } else {
          contractPeriod = translation['label_end_date'] + `: ${endDate}`
        }
      } else if (device.license_type === 'term') {
        contractPeriod = `${startDate} - ${endDate}`
      }
      if (!isLicenseActive) {
        contractPeriod = translation['label_license_not_assigned']
      }

      return {
        ...device,
        deviceType,
        contractPeriod,
        isLicenseActive,
      }
    })
  }, [devices, translation])

  const handleToggleOne = (deviceId: string) => {
    setSelectedDeviceIds(prev =>
      prev.includes(deviceId) ? prev.filter(id => id !== deviceId) : [...prev, deviceId],
    )
  }

  const handleDelete = async () => {
    setDeleteDialogOpen(false)
    if (selectedDeviceIds.length === 0) return

    const batchSize = 5
    const results: { deviceId: string; result: Awaited<ReturnType<typeof c215.execDeviceDelete>> }[] = []

    for (let i = 0; i < selectedDeviceIds.length; i += batchSize) {
      const batch = selectedDeviceIds.slice(i, i + batchSize)
      const batchResults = await Promise.all(
        batch.map(async deviceId => {
          const result = await c215.execDeviceDelete(auth.tenant, deviceId)
          return { deviceId, result }
        }),
      )
      results.push(...batchResults)
    }

    const failed = results.filter(r => !r.result.success)
    if (failed.length > 0) {
      const lines = failed.map(f => {
        const cotopatId = devices.find(d => d.device_id === f.deviceId)?.cotopat_id ?? f.deviceId
        const messageKey =
          f.result.message === 'label_not_found_target_device'
            ? 'message_target_device_not_found'
            : f.result.message
        const msg = messageKey ? (translation[messageKey] ?? errorMsg.SYSTEM_ERROR) : errorMsg.SYSTEM_ERROR
        return translation['label_cotopat_id'] + `: ${cotopatId} - ${msg}`
      })
      setErrorDialogText(lines.join('\n'))
      setOpenErrorDialog(true)
    } else {
      setSelectedDeviceIds([])
      setDeleteSuccessNoticeOpen(true)
    }
    await refreshDevices()
  }

  const executeImport = async (targetDeviceIds: string[], file: File) => {
    const signedUrlResult = await c902.execSignedUrlGet(auth.tenant)
    if (!signedUrlResult.success) {
      throw new Error(signedUrlResult.message ?? errorMsg.SYSTEM_ERROR)
    }

    const signedUrl = signedUrlResult.message!

    const uploadResult = await cs.execUploadFile(signedUrl, file)
    if (!uploadResult.success) {
      throw new Error(uploadResult.message ?? errorMsg.SYSTEM_ERROR)
    }

    const importResult = await c218.execDeviceBulkImport(
      targetDeviceIds,
      signedUrl,
      auth.tenant,
    )
    if (!importResult.success) {
      throw new Error(importResult.message ?? errorMsg.SYSTEM_ERROR)
    }
  }

  const handleImportSelected = () => {
    if (selectedDeviceIds.length === 0) {
      return
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    fileInputRef.current?.click()
  }

  const handleImportFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    if (!validateImportFileSize(file)) {
      setErrorDialogText(errorMsg.IMPORT_FILE_SIZE_ERROR)
      setOpenErrorDialog(true)
      return
    }

    if (!zipPattern.test(file.name)) {
      setErrorDialogText(errorMsg.UNSUPPORTED_FILE_ERROR)
      setOpenErrorDialog(true)
      return
    }

    try {
      await executeImport(selectedDeviceIds, file)
      await refreshDevices()
      setSelectedDeviceIds([])
      setImportSuccessNoticeOpen(true)
    } catch (error) {
      if (error instanceof Error) {
        setErrorDialogText(error.message)
        setOpenErrorDialog(true)
      }
    }
  }

  const handleMemoChange = (deviceId: string, value: string) => {
    setMemoMap(prev => ({ ...prev, [deviceId]: value }))
  }

  const handleMemoBlur = async (deviceId: string) => {
    const originalMemo = devices.find(device => device.device_id === deviceId)?.memo ?? ''
    const normalizedMemo = (memoMap[deviceId] ?? '').normalize('NFC')
    const currentMemo = normalizedMemo.length > 255 ? normalizedMemo.slice(0, 255) : normalizedMemo

    if (currentMemo === originalMemo) {
      return
    }

    const result = await c214.execDeviceMemoUpdate(deviceId, currentMemo)
    if (!result.success) {
      setErrorDialogText(result.message ?? errorMsg.SYSTEM_ERROR)
      setOpenErrorDialog(true)
      return
    }

    setDevices(prev =>
      prev.map(device =>
        device.device_id === deviceId ? { ...device, memo: currentMemo } : device,
      ),
    )
    setMemoMap(prev => ({ ...prev, [deviceId]: currentMemo }))
  }

  const goToCotopatWeb = (deviceId: string) => {
    auth.setDeviceId(deviceId)
    const device = devices.find(d => d.device_id === deviceId)
    auth.setRhq(device?.rhq ?? '')
    auth.setSalesCompany(device?.sales_company ?? '')
    navigate(`/phrase?deviceId=${encodeURIComponent(deviceId)}`)
  }

  const handleGroupAssignmentSaved = async () => {
    await refreshDevices()
    setSelectedDeviceIds([])
    setGroupSaveNoticeOpen(true)
  }

  const handleOperationPermissionSaved = async () => {
    setOperationSaveNoticeOpen(true)
  }

  const totalPages = useMemo(() => {
    const pageCount = Math.ceil(parsedRows.length / itemsPerPage)
    return pageCount > 0 ? pageCount : 1
  }, [parsedRows, itemsPerPage])

  useEffect(() => {
    setCurrentPage(prev => Math.min(prev, totalPages))
  }, [totalPages])

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    return parsedRows.slice(start, start + itemsPerPage)
  }, [parsedRows, currentPage, itemsPerPage])

  const allSelected = pagedRows.length > 0 && pagedRows.every(row => selectedDeviceIds.includes(row.device_id))

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedDeviceIds(prev =>
        prev.filter(id => !pagedRows.find(row => row.device_id === id))
      )
      return
    }
    const pageIds = pagedRows.map(row => row.device_id)
    setSelectedDeviceIds(prev => {
      const set = new Set(prev)
      pageIds.forEach(id => set.add(id))
      return Array.from(set)
    })
  }

  const visiblePages = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, 5]
    }

    if (currentPage >= totalPages - 2) {
      return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
    }

    return [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2]
  }, [currentPage, totalPages])

  const displayRangeStart = parsedRows.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1
  const displayRangeEnd = Math.min(currentPage * itemsPerPage, parsedRows.length)

  const handleFirstPage = () => {
    setCurrentPage(1)
  }

  const handlePrevPage = () => {
    setCurrentPage(prev => (prev === 1 ? totalPages : prev - 1))
  }

  const handleNextPage = () => {
    setCurrentPage(prev => (prev === totalPages ? 1 : prev + 1))
  }

  const handleLastPage = () => {
    setCurrentPage(totalPages)
  }

  const headerDividerSx = {
    borderBottom: '2px solid #3e3e3e',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: '25%',
      height: '50%',
      borderRight: '1px solid #ccc',
    },
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        backgroundColor: '#ececec',
      }}
    >
      {openErrorDialog && (
        <ModalConfirmDialogDCP
          open={openErrorDialog}
          text={errorDialogText}
          onClick={() => setOpenErrorDialog(false)}
          buttonText='button_close'
        />
      )}

      <Snackbar
        open={groupSaveNoticeOpen}
        autoHideDuration={3000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return
          setGroupSaveNoticeOpen(false)
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 1 }}
      >
        <Alert
          onClose={() => setGroupSaveNoticeOpen(false)}
          severity="success"
          variant="filled"
          sx={successSnackbarAlertSx}
        >
          {translation['message_device_access_setting_saved']}
        </Alert>
      </Snackbar>

      <Snackbar
        open={operationSaveNoticeOpen}
        autoHideDuration={3000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return
          setOperationSaveNoticeOpen(false)
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 1 }}
      >
        <Alert
          onClose={() => setOperationSaveNoticeOpen(false)}
          severity='success'
          variant='filled'
          sx={successSnackbarAlertSx}
        >
          {translation['message_user_admin_permission_saved']}
        </Alert>
      </Snackbar>

      <Snackbar
        open={deleteSuccessNoticeOpen}
        autoHideDuration={5000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return
          setDeleteSuccessNoticeOpen(false)
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 1 }}
      >
        <Alert
          onClose={() => setDeleteSuccessNoticeOpen(false)}
          severity='success'
          variant='filled'
          sx={successSnackbarAlertSx}
        >
          {translation['message_device_deleted']}
        </Alert>
      </Snackbar>

      <Snackbar
        open={importSuccessNoticeOpen}
        autoHideDuration={3000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return
          setImportSuccessNoticeOpen(false)
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 1 }}
      >
        <Alert
          onClose={() => setImportSuccessNoticeOpen(false)}
          severity='success'
          variant='filled'
          sx={successSnackbarAlertSx}
        >
          {translation['message_data_imported']}
        </Alert>
      </Snackbar>

      {loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.35)',
            pointerEvents: 'auto',
            cursor: 'wait',
            zIndex: 100,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{translation['button_delete']}</DialogTitle>
        <DialogContent>
          <Typography>{translation['confirmation_delete_device']}</Typography>
        </DialogContent>
        <DialogActions sx={dialogActionsSx(3)}>
          <Button onClick={() => setDeleteDialogOpen(false)}
            sx={dialogCancelButtonSx}
          >
            {translation['button_cancel']}
          </Button>
          <Button onClick={handleDelete} variant="contained"
            sx={dialogSaveButtonSx(true)}
          >
            {translation['button_delete']}
          </Button>
        </DialogActions>
      </Dialog>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".zip"
        onChange={handleImportFileChange}
      />

      <Box sx={{ px: { xs: 3, md: 6 }, pt: 2 }}>
        <Typography sx={{ font: 'normal normal bold 34px/40px Noto Sans JP', color: '#202020' }}>
          Cotopat
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 3, md: 6 },
          pt: 2,
          maxWidth: 1160,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(100vh - 110px)',
        }}>
        <Typography sx={{ font: 'normal normal bold 28px/36px Noto Sans JP', color: '#202020' }}>
          {translation['label_device_list_title']}
        </Typography>
        <Typography sx={{ mt: 1, font: 'normal normal normal 1rem/1.5 Noto Sans JP', color: '#202020' }}>
          {translation['message_device_list_description']}
        </Typography>
        {canManageDevices && (
          <Typography sx={{ font: 'normal normal normal 1rem/1.5 Noto Sans JP', color: '#202020' }}>
            {translation['message_device_list_description2']}
          </Typography>
        )}

        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: { xs: 2, md: 4 },
            borderRadius: '12px',
            backgroundColor: '#f7f7f7',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {canManageDevices && (
                <Button
                  startIcon={<DeleteIcon />}
                  variant="contained"
                  disabled={selectedDeviceIds.length === 0 || loading}
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={deviceListActionButtonSx}
                >
                  {translation['button_delete_device']}
                </Button>
              )}

              {canManageDevices && (
                <Button
                  startIcon={<FileUploadOutlinedIcon />}
                  variant="contained"
                  disabled={selectedDeviceIds.length === 0 || loading}
                  onClick={handleImportSelected}
                  sx={deviceListActionButtonSx}
                >
                  {translation['button_import_data']}
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {canManageDevices && (
                <Button
                  startIcon={<GroupIcon />}
                  variant="contained"
                  disabled={loading}
                  onClick={() => setGroupDialogOpen(true)}
                  sx={deviceListActionButtonSx}
                >
                  {translation['button_view_permission_setting']}
                </Button>
              )}

              {canManageDevices && (
                <Button
                  startIcon={<SettingsIcon />}
                  variant='contained'
                  disabled={loading}
                  onClick={() => setOperationDialogOpen(true)}
                  sx={deviceListActionButtonSx}
                >
                  {translation['button_operation_permission_setting']}
                </Button>
              )}
            </Box>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ width: 56, ...headerDividerSx }}
                  >
                    <Checkbox
                      checked={allSelected}
                      onChange={handleToggleAll}
                      disabled={!canManageDevices}
                      sx={checkboxPrimarySx}
                    />
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, ...headerDividerSx }}
                  >
                    {translation['label_device_type']}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, ...headerDividerSx }}
                  >
                    {translation['label_cotopat_id']}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, ...headerDividerSx }}
                  >
                    {translation['label_contract_period']}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, ...headerDividerSx }}
                  >
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <Box component="span">{translation['label_memo_input']}</Box>
                      <Tooltip
                        title={translation['label_memo_description']}
                        arrow
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 20, color: '#0a9bcd', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, borderBottom: '2px solid #3e3e3e' }}>
                    {translation['label_transition_web']}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map(row => {
                  const memoValue = memoMap[row.device_id] ?? ''
                  const rowTextColor = row.isLicenseActive ? '#202020' : '#d32f2f'
                  const displayCotopatId = row.cotopat_id || row.device_id

                  return (
                    <TableRow key={row.device_id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedDeviceIds.includes(row.device_id)}
                          onChange={() => handleToggleOne(row.device_id)}
                          disabled={!canManageDevices}
                          sx={checkboxPrimarySx}
                        />
                      </TableCell>
                      <TableCell sx={{ color: rowTextColor }}>{row.deviceType}</TableCell>
                      <TableCell sx={{ color: rowTextColor }}>{displayCotopatId}</TableCell>
                      <TableCell sx={{ color: rowTextColor }}>
                        <Box>{row.contractPeriod}</Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={memoValue} arrow disableHoverListener={!memoValue}>
                          <Box>
                            <TextField
                              size="small"
                              value={memoValue}
                              onChange={event => {
                                if (!row.isLicenseActive) {
                                  return
                                }
                                handleMemoChange(row.device_id, event.target.value)
                              }}
                              onBlur={() => {
                                void handleMemoBlur(row.device_id)
                              }}
                              inputProps={{
                                maxLength: 255,
                                readOnly: !row.isLicenseActive,
                              }}
                              placeholder={translation['label_memo_placeholder']}
                              sx={{
                                width: 240,
                                '& .MuiInputBase-input': {
                                  color: row.isLicenseActive ? 'inherit' : '#d32f2f',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                },
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          disabled={!row.isLicenseActive}
                          onClick={() => goToCotopatWeb(row.device_id)}
                        >
                          <OpenInNewIcon
                            sx={{ color: row.isLicenseActive ? '#0a9bcd' : 'action.disabled' }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, position: 'relative', display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto 1fr', alignItems: 'center', columnGap: 2 }}>
            <Box sx={{ gridColumn: 3, justifySelf: 'center' }}>
              <Box sx={{ display: 'inline-flex', border: '1px solid #d6d6d6', backgroundColor: '#ffffff' }}>
                <IconButton
                  onClick={handleFirstPage}
                  size='small'
                  disabled={loading || totalPages <= 1 || currentPage === 1}
                  sx={{ width: 28, height: 28, borderRadius: 0, borderRight: '1px solid #d6d6d6' }}
                >
                  <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  onClick={handlePrevPage}
                  size='small'
                  disabled={loading || totalPages <= 1 || currentPage === 1}
                  sx={{ width: 28, height: 28, borderRadius: 0, borderRight: '1px solid #d6d6d6' }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 20 }} />
                </IconButton>

                {visiblePages.map(page => (
                  <Button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    disabled={loading}
                    sx={{
                      minWidth: 28,
                      width: 28,
                      height: 28,
                      borderRadius: 0,
                      borderRight: '1px solid #d6d6d6',
                      color: page === currentPage ? '#fff' : '#202020',
                      backgroundColor: page === currentPage ? '#0a9bcd' : 'transparent',
                      '&:hover': {
                        backgroundColor: page === currentPage ? '#0a9bcd' : '#e9e9e9',
                      },
                    }}
                  >
                    {page}
                  </Button>
                ))}

                <IconButton
                  onClick={handleNextPage}
                  size='small'
                  disabled={loading || totalPages <= 1 || currentPage === totalPages}
                  sx={{ width: 28, height: 28, borderRadius: 0, borderRight: '1px solid #d6d6d6' }}
                >
                  <ChevronRightIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  onClick={handleLastPage}
                  size='small'
                  disabled={loading || totalPages <= 1 || currentPage === totalPages}
                  sx={{ width: 28, height: 28, borderRadius: 0 }}
                >
                  <KeyboardDoubleArrowRightIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ gridColumn: 4, justifySelf: 'end', display: 'flex', alignItems: 'center', gap: 0 }}>
              <Typography sx={{ fontSize: '0.875rem', color: '#666' }}>
                {translation['label_rows_per_page']}
              </Typography>
              <Select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value))
                  setCurrentPage(1)
                }}
                size='small'
                sx={{
                  width: 80,
                  '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiSelect-icon': { right: '12px !important' },
                  '& > fieldset': { border: 'none !important' },
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={500}>500</MenuItem>
              </Select>
            </Box>

            <Typography sx={{ gridColumn: 5, justifySelf: 'end', fontSize: '0.875rem', color: '#666' }}>
              {translation['label_display_count']} {displayRangeStart}-{displayRangeEnd} / {parsedRows.length}
            </Typography>
          </Box>
        </Paper>
      </Box>

      <GroupAssignmentDialog
        open={groupDialogOpen}
        tenant={auth.tenant}
        devices={devices}
        onClose={() => setGroupDialogOpen(false)}
        onSave={handleGroupAssignmentSaved}
      />

      <OperationPermissionDialog
        open={operationDialogOpen}
        tenant={auth.tenant}
        onClose={() => setOperationDialogOpen(false)}
        onSave={handleOperationPermissionSaved}
      />

      <FooterDCP fixed={true} />
    </Box>
  )
}
